import type { ReactNode } from 'react'

export interface ChoiceOption<T extends string> {
  value: T
  title: string
  subtitle?: string
  icon: ReactNode
}

/** A row of single-select big icon buttons. Selecting one highlights it. */
export const ChoiceCardGroup = <T extends string>({
  options,
  value,
  onChange,
}: {
  options: ChoiceOption<T>[]
  value: T | null
  onChange: (v: T) => void
}) => (
  <div className="flex flex-wrap items-stretch justify-center gap-4">
    {options.map((opt) => {
      const active = value === opt.value
      return (
        <button
          key={opt.value}
          type="button"
          aria-pressed={active}
          onClick={() => onChange(opt.value)}
          className={`flex w-44 flex-col items-center gap-3 rounded-2xl border-2 p-6 text-center transition-all ${
            active
              ? 'border-accent bg-accent/10 text-foreground shadow-sm'
              : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-primary'
          }`}
        >
          <span
            className={`grid h-[68px] w-[68px] place-items-center rounded-xl ${
              active ? 'bg-accent/15 text-accent' : 'bg-foreground/[0.04]'
            }`}
          >
            {opt.icon}
          </span>
          <span className="text-base font-semibold">{opt.title}</span>
          {opt.subtitle !== undefined && <span className="text-xs text-muted-foreground">{opt.subtitle}</span>}
        </button>
      )
    })}
  </div>
)
